import { createSlice } from "@reduxjs/toolkit";
import undoable, { includeAction, ActionCreators } from "redux-undo";
import { notesCreatedOrUpdated, noteModuleName, notesApi, isFetchNotesAction } from "./api";
import { createReducerRegistry } from "components/high-order-components/insComponentWithSlice";
import { generateId } from "utils/unsaved-changes/dialogRegistry";
import { mergeFetchedNotesIntoEditors, mergeUpsertedNotesIntoEditors, removeEditorsNotInFetch } from "./reducer-helpers";
import { getEditorKeyByNote } from "./util";
const initialState = {
    editors: {},
    error: null,
    isLoading: false,
    notes: [],
    notesList: [],
    savedEditors: {}
};
const selectFirstCurrentNoteIfMissing = (state) => {
    if (!state.editors || state.currentNoteKey) {
        return;
    }
    const [firstKey] = Object.keys(state.editors);
    state.currentNoteKey = firstKey ?? undefined;
};
export const createDefaultNote = (entityId, entityType, additionalEntityId) => {
    const today = new Date();
    return {
        additionalEntityId,
        content: "",
        createdAt: today,
        createdBy: "",
        documents: [],
        entityId,
        entityType,
        referenceDate: {
            day: today.getDate(),
            month: today.getMonth() + 1,
            year: today.getFullYear()
        },
        title: "New Note",
        updatedAt: today,
        updatedBy: ""
    };
};
export const noteSlice = createSlice({
    extraReducers: (builder) => {
        builder.addMatcher(isFetchNotesAction, (state, { payload }) => {
            state.isLoading = false;
            const notes = payload.notes;
            if (notes.length === 0) {
                state.editors = {};
                state.savedEditors = {};
                state.currentNoteKey = undefined;
                return;
            }
            const fetchedIds = new Set();
            notes.forEach((note) => {
                if (typeof note.id === "number") {
                    fetchedIds.add(note.id);
                }
            });
            const nextEditors = removeEditorsNotInFetch(state, fetchedIds);
            mergeFetchedNotesIntoEditors(state, nextEditors, notes);
            state.editors = nextEditors;
            state.savedEditors = { ...nextEditors };
            selectFirstCurrentNoteIfMissing(state);
        })
            .addMatcher(notesCreatedOrUpdated, (state, action) => {
            state.isLoading = false;
            const { payload } = action;
            if (payload.notes.length === 0) {
                return;
            }
            mergeUpsertedNotesIntoEditors(state, action, selectFirstCurrentNoteIfMissing);
            Object.keys(state.editors ?? {}).forEach((key) => {
                const editor = state.editors?.[key];
                const wasSaved = payload.notes.some((savedNote) => savedNote.id === editor?.id || savedNote.noteKey === key);
                if (wasSaved && editor) {
                    if (!state.savedEditors) {
                        state.savedEditors = {};
                    }
                    state.savedEditors[key] = { ...editor };
                }
            });
        });
    },
    initialState,
    name: noteModuleName,
    reducers: {
        addPendingDocument: (state, action) => {
            const { noteKey, document } = action.payload;
            if (state.editors?.[noteKey]) {
                state.editors[noteKey].documents.push(document);
            }
        },
        cancelNote: (state, action) => {
            if (state.editors && action.payload) {
                delete state.editors[action.payload];
                const keys = Object.keys(state.editors);
                if (keys.length === 0) {
                    state.currentNoteKey = undefined;
                }
                else {
                    const [firstKey] = keys;
                    state.currentNoteKey = firstKey;
                }
            }
        },
        discardAllChanges: (state) => {
            if (state.savedEditors) {
                state.editors = { ...state.savedEditors };
            }
        },
        discardNoteChanges: (state, action) => {
            const key = action.payload;
            if (key && state.savedEditors?.[key] && state.editors) {
                state.editors[key] = { ...state.savedEditors[key] };
            }
        },
        editNote: (state, action) => {
            const note = action.payload;
            const key = getEditorKeyByNote(state.editors, note);
            if (state.editors && key) {
                state.editors[key] = { ...note };
            }
        },
        initializeForCreate: (state, action) => {
            const tempId = generateId();
            const newNote = {
                noteKey: tempId,
                ...createDefaultNote(action.payload.entityId, action.payload.entityType, action.payload.additionalEntityId)
            };
            if (state.editors) {
                state.editors = {
                    [tempId]: newNote,
                    ...state.editors
                };
            }
            else {
                state.editors = {
                    [tempId]: newNote
                };
            }
            state.currentNoteKey = tempId;
        },
        removeDocument: (state, action) => {
            if (!state.editors) {
                return;
            }
            Object.values(state.editors).forEach((note) => {
                note.documents = note.documents.filter((d) => d.id !== action.payload);
            });
        },
        setCurrentNoteById: (state, action) => {
            if (state.editors && action.payload && state.editors[action.payload]) {
                state.currentNoteKey = action.payload;
            }
        },
        setDocumentResourceId: (state, action) => {
            const { documentId, resourceId } = action.payload;
            if (!state.editors) {
                return;
            }
            for (const note of Object.values(state.editors)) {
                const doc = note.documents.find((d) => d.id === documentId);
                if (doc) {
                    doc.resourceId = resourceId;
                    return;
                }
            }
        },
        setDocumentStatus: (state, action) => {
            const { documentId, status } = action.payload;
            if (!state.editors) {
                return;
            }
            for (const note of Object.values(state.editors)) {
                const doc = note.documents.find((d) => d.id === documentId);
                if (doc) {
                    doc.status = status;
                    return;
                }
            }
        },
        setDocumentUploadId: (state, action) => {
            const { documentId, uploadId } = action.payload;
            if (!state.editors) {
                return;
            }
            for (const note of Object.values(state.editors)) {
                const doc = note.documents.find((d) => d.id === documentId);
                if (doc) {
                    doc.uploadId = uploadId;
                    return;
                }
            }
        },
        updateFormField: (state, action) => {
            if (state.editors && action.payload.key) {
                const { key } = action.payload;
                if (key) {
                    const note = state.editors[key];
                    if (note) {
                        state.editors[key] = {
                            ...note,
                            [action.payload.field]: action.payload.value
                        };
                    }
                }
            }
        }
    }
});
const baseNoteReducer = noteSlice.reducer;
export const noteReducer = undoable(baseNoteReducer, {
    filter: includeAction(Object.keys(noteSlice.actions).map((action) => `${noteModuleName}/${action}`))
});
export { ActionCreators as UndoActionCreators };
export const { addPendingDocument, cancelNote, discardAllChanges, discardNoteChanges, editNote, initializeForCreate, removeDocument, setCurrentNoteById, setDocumentResourceId, setDocumentStatus, setDocumentUploadId, updateFormField } = noteSlice.actions;
const undoableInitialState = {
    _latestUnfiltered: initialState,
    future: [],
    group: null,
    index: 0,
    limit: 100,
    past: [],
    present: initialState
};
export const EnhancedNotes = createReducerRegistry([
    {
        initialState: undoableInitialState,
        key: noteModuleName,
        name: "Notes",
        reducer: noteReducer
    },
    {
        key: notesApi.reducerPath,
        name: "Notes API",
        reducer: notesApi.reducer
    }
]);
